import { Link } from 'react-router-dom';
import Navigation from '../../../components/feature/Navigation';

export default function EmergencyShelterLuxembourg() {
  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      
      {/* Hero Section */}
      <div className="relative h-[400px] overflow-hidden">
        <img
          src="https://public.readdy.ai/ai/img_res/5be7586a9b83f5a4f409fa88ebb2537d.jpg"
          alt="Safe shelter"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center text-white px-4 max-w-4xl">
            <h1 className="text-5xl font-bold mb-4">Where to Go When You Need Shelter in Luxembourg</h1>
            <p className="text-xl mb-2">A Complete Guide to Emergency Housing, Insurance Support & Long-Term Solutions</p>
            <div className="flex items-center justify-center gap-4 text-sm mt-6">
              <span className="flex items-center gap-2">
                <i className="ri-calendar-line"></i>
                January 15, 2025
              </span>
              <span className="flex items-center gap-2">
                <i className="ri-time-line"></i>
                12 min read
              </span>
              <span className="flex items-center gap-2">
                <i className="ri-bookmark-line"></i>
                Emergency Resources
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Personal Introduction */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-8 mb-12 border-l-4 border-purple-500">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-purple-500 rounded-full flex items-center justify-center flex-shrink-0">
              <i className="ri-heart-3-fill text-white text-xl"></i>
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Why I Wrote This Guide</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                I know exactly how stressful it is not knowing where you're going to stay. I've been there myself. After I had to leave my father's house and live alone, I remember the overwhelming anxiety of trying to find somewhere safe to go. The uncertainty, the fear, the endless questions running through my mind at night.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                I know how difficult it was to find somewhere—calling numbers that didn't answer, reading confusing websites, not knowing if I qualified for help, wondering if I had enough money, feeling completely lost in a system that seemed impossible to navigate.
              </p>
              <p className="text-gray-700 leading-relaxed font-semibold">
                That's why I'm here to write this. So you don't have to feel as lost as I did. So you have all the information in one place. So you know exactly where to go, who to call, and what help is available to you right now.
              </p>
              <p className="text-purple-600 font-semibold mt-4 italic">
                You're not alone. I understand. And there is help available. Let me show you where. 💜
              </p>
            </div>
          </div>
        </div>

        {/* Emergency Alert Box */}
        <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-lg mb-12">
          <div className="flex gap-4">
            <i className="ri-alarm-warning-line text-3xl text-red-600 flex-shrink-0"></i>
            <div>
              <h3 className="text-xl font-bold text-red-900 mb-2">
                In Immediate Danger? Call Now:
              </h3>
              <div className="space-y-2 text-red-800">
                <p className="text-lg"><strong>Police Emergency:</strong> 113</p>
                <p className="text-lg"><strong>Women's Helpline:</strong> 2060 1060 (24/7)</p>
                <p className="text-lg"><strong>Femmes en Détresse:</strong> 40 81 81 (24/7)</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <article className="max-w-4xl mx-auto px-6 py-16">
        {/* Introduction */}
        <div className="prose prose-lg max-w-none mb-16">
          <p className="text-xl text-gray-700 leading-relaxed">
            If you're reading this, you might be in a situation where you need to leave your home urgently. 
            You're not alone, and there are places that will help you—no questions asked, no judgment. 
            This guide will show you exactly where to go and what support is available in Luxembourg.
          </p>
        </div>

        {/* Immediate Shelter Options */}
        <section className="mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-8 flex items-center gap-3">
            <i className="ri-home-heart-line text-purple-600"></i>
            Immediate Shelter Options (24/7)
          </h2>

          {/* Femmes en Détresse */}
          <div className="bg-purple-50 rounded-2xl p-8 mb-6">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-16 h-16 bg-purple-600 rounded-full flex items-center justify-center flex-shrink-0">
                <i className="ri-shield-check-line text-2xl text-white"></i>
              </div>
              <div className="flex-1">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  Femmes en Détresse (Women in Distress)
                </h3>
                <p className="text-purple-900 font-medium mb-4">
                  The primary emergency shelter for women and children fleeing domestic violence
                </p>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div>
                <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                  <i className="ri-phone-line text-purple-600"></i>
                  Contact Information
                </h4>
                <ul className="space-y-2 text-gray-700">
                  <li><strong>24/7 Hotline:</strong> 40 81 81</li>
                  <li><strong>Email:</strong> info@fed.lu</li>
                  <li><strong>Website:</strong> www.fed.lu</li>
                  <li><strong>Address:</strong> Confidential (given upon contact)</li>
                </ul>
              </div>

              <div>
                <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                  <i className="ri-service-line text-purple-600"></i>
                  What They Provide
                </h4>
                <ul className="space-y-2 text-gray-700">
                  <li>✅ Emergency accommodation (up to 3 months)</li>
                  <li>✅ Safe space for you and your children</li>
                  <li>✅ Psychological support and counseling</li>
                  <li>✅ Legal advice and assistance</li>
                  <li>✅ Help finding permanent housing</li>
                  <li>✅ Support with administrative procedures</li>
                </ul>
              </div>
            </div>

            <div className="bg-white rounded-xl p-6">
              <h4 className="font-bold text-gray-900 mb-3">Important to Know:</h4>
              <ul className="space-y-2 text-gray-700">
                <li>• <strong>No cost</strong> - All services are free</li>
                <li>• <strong>Confidential location</strong> - Address is protected for your safety</li>
                <li>• <strong>Multilingual staff</strong> - French, German, English, Portuguese, and more</li>
                <li>• <strong>Children welcome</strong> - They have space and support for children of all ages</li>
                <li>• <strong>No ID required initially</strong> - They'll help you get documents later</li>
              </ul>
            </div>
          </div>

          {/* Fraenhaus */}
          <div className="bg-pink-50 rounded-2xl p-8 mb-6">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-16 h-16 bg-pink-600 rounded-full flex items-center justify-center flex-shrink-0">
                <i className="ri-home-smile-line text-2xl text-white"></i>
              </div>
              <div className="flex-1">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  Fraenhaus (Women's House)
                </h3>
                <p className="text-pink-900 font-medium mb-4">
                  Another emergency shelter option with comprehensive support services
                </p>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-bold text-gray-900 mb-3">Contact:</h4>
                <ul className="space-y-2 text-gray-700">
                  <li><strong>Phone:</strong> 44 81 81</li>
                  <li><strong>Email:</strong> info@fraenhaus.lu</li>
                  <li><strong>Available:</strong> 24/7 emergency line</li>
                </ul>
              </div>

              <div>
                <h4 className="font-bold text-gray-900 mb-3">Services:</h4>
                <ul className="space-y-2 text-gray-700">
                  <li>✅ Emergency shelter</li>
                  <li>✅ Crisis intervention</li>
                  <li>✅ Counseling and therapy</li>
                  <li>✅ Legal support</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Insurance Support */}
        <section className="mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-8 flex items-center gap-3">
            <i className="ri-shield-user-line text-blue-600"></i>
            Insurance-Supported Housing
          </h2>

          <div className="bg-blue-50 rounded-2xl p-8 mb-6">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                <i className="ri-building-line text-2xl text-white"></i>
              </div>
              <div className="flex-1">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  AXA Assurances Luxembourg
                </h3>
                <p className="text-blue-900 font-medium">
                  Some insurance policies include emergency housing assistance
                </p>
              </div>
            </div>

            <div className="bg-white rounded-xl p-6 mb-6">
              <h4 className="font-bold text-gray-900 mb-4">How It Works:</h4>
              <div className="space-y-4 text-gray-700">
                <p>
                  <strong>Home Insurance Coverage:</strong> Many comprehensive home insurance policies 
                  (including those from AXA) include temporary accommodation coverage if your home becomes 
                  uninhabitable due to an emergency situation.
                </p>
                <p>
                  <strong>What's Covered:</strong> Typically includes hotel accommodation, temporary rental, 
                  or emergency housing for a specified period (usually 30-90 days) while you find permanent 
                  housing.
                </p>
                <p>
                  <strong>Domestic Violence Situations:</strong> While traditionally for fire/flood damage, 
                  some insurers are beginning to recognize domestic violence as a valid emergency requiring 
                  temporary housing assistance.
                </p>
              </div>
            </div>

            <div className="bg-blue-100 rounded-xl p-6">
              <h4 className="font-bold text-gray-900 mb-3">Steps to Access Insurance Support:</h4>
              <ol className="space-y-3 text-gray-700">
                <li className="flex gap-3">
                  <span className="font-bold text-blue-600 flex-shrink-0">1.</span>
                  <span><strong>Check your policy:</strong> Review your home insurance documents or call your insurer</span>
                </li>
                <li className="flex gap-3">
                  <span className="font-bold text-blue-600 flex-shrink-0">2.</span>
                  <span><strong>Contact AXA:</strong> Call +352 44 24 24 1 (24/7 emergency line)</span>
                </li>
                <li className="flex gap-3">
                  <span className="font-bold text-blue-600 flex-shrink-0">3.</span>
                  <span><strong>Explain your situation:</strong> Be clear that you need emergency housing due to domestic violence</span>
                </li>
                <li className="flex gap-3">
                  <span className="font-bold text-blue-600 flex-shrink-0">4.</span>
                  <span><strong>Get police report:</strong> Having filed a police report (113) strengthens your claim</span>
                </li>
                <li className="flex gap-3">
                  <span className="font-bold text-blue-600 flex-shrink-0">5.</span>
                  <span><strong>Request immediate assistance:</strong> Ask for emergency hotel accommodation while they process your claim</span>
                </li>
              </ol>
            </div>

            <div className="mt-6 bg-yellow-50 border-l-4 border-yellow-500 p-4">
              <p className="text-sm text-yellow-900">
                <strong>Note:</strong> Not all policies cover domestic violence situations. If your insurer 
                refuses, contact Femmes en Détresse immediately—they provide free shelter regardless of insurance.
              </p>
            </div>
          </div>

          {/* Other Insurance Providers */}
          <div className="bg-gray-50 rounded-2xl p-8">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Other Insurance Providers to Contact:</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-white rounded-xl p-4">
                <h4 className="font-bold text-gray-900 mb-2">Foyer Assurances</h4>
                <p className="text-sm text-gray-600 mb-2">Emergency: +352 437 43 1</p>
                <p className="text-sm text-gray-700">May provide temporary housing under home insurance policies</p>
              </div>
              <div className="bg-white rounded-xl p-4">
                <h4 className="font-bold text-gray-900 mb-2">Baloise Assurances</h4>
                <p className="text-sm text-gray-600 mb-2">Emergency: +352 290 190</p>
                <p className="text-sm text-gray-700">Check if your policy includes emergency accommodation</p>
              </div>
              <div className="bg-white rounded-xl p-4">
                <h4 className="font-bold text-gray-900 mb-2">La Luxembourgeoise</h4>
                <p className="text-sm text-gray-600 mb-2">Emergency: +352 29 59 59 1</p>
                <p className="text-sm text-gray-700">Some policies cover temporary housing in emergencies</p>
              </div>
              <div className="bg-white rounded-xl p-4">
                <h4 className="font-bold text-gray-900 mb-2">Lalux Assurances</h4>
                <p className="text-sm text-gray-600 mb-2">Emergency: +352 29 29 29 1</p>
                <p className="text-sm text-gray-700">May assist with emergency accommodation costs</p>
              </div>
            </div>
          </div>
        </section>

        {/* Government Housing Support */}
        <section className="mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-8 flex items-center gap-3">
            <i className="ri-government-line text-green-600"></i>
            Government Housing Assistance
          </h2>

          <div className="bg-green-50 rounded-2xl p-8 mb-6">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">
              Fonds du Logement (Housing Fund)
            </h3>

            <div className="bg-white rounded-xl p-6 mb-6">
              <h4 className="font-bold text-gray-900 mb-4">Emergency Housing Priority:</h4>
              <p className="text-gray-700 mb-4">
                Women fleeing domestic violence are given <strong>priority status</strong> for social housing 
                in Luxembourg. The Fonds du Logement can help you access affordable housing quickly.
              </p>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h5 className="font-bold text-gray-900 mb-3">Contact:</h5>
                  <ul className="space-y-2 text-gray-700">
                    <li><strong>Phone:</strong> +352 40 19 40 1</li>
                    <li><strong>Email:</strong> info@fonds-logement.lu</li>
                    <li><strong>Address:</strong> 8, rue de l'Eau, L-1449 Luxembourg</li>
                    <li><strong>Hours:</strong> Mon-Fri, 8:30-12:00 & 13:30-17:00</li>
                  </ul>
                </div>

                <div>
                  <h5 className="font-bold text-gray-900 mb-3">What They Offer:</h5>
                  <ul className="space-y-2 text-gray-700">
                    <li>✅ Affordable rental apartments</li>
                    <li>✅ Priority for violence victims</li>
                    <li>✅ Rent subsidies available</li>
                    <li>✅ Long-term housing solutions</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="bg-green-100 rounded-xl p-6">
              <h4 className="font-bold text-gray-900 mb-3">How to Apply:</h4>
              <ol className="space-y-3 text-gray-700">
                <li>1. Contact Femmes en Détresse or your social worker for a priority letter</li>
                <li>2. Gather documents: ID, proof of income, police report (if filed)</li>
                <li>3. Submit application to Fonds du Logement</li>
                <li>4. Mention you're fleeing domestic violence for priority processing</li>
                <li>5. They'll help you find suitable housing within weeks (not months)</li>
              </ol>
            </div>
          </div>

          {/* Municipal Housing */}
          <div className="bg-teal-50 rounded-2xl p-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Municipal Social Services (SCAS)
            </h3>
            <p className="text-gray-700 mb-6">
              Each Luxembourg municipality has a social service office that can help with emergency housing, 
              financial assistance, and connecting you to resources.
            </p>

            <div className="grid md:grid-cols-3 gap-4">
              <div className="bg-white rounded-xl p-4">
                <h4 className="font-bold text-gray-900 mb-2">Luxembourg City</h4>
                <p className="text-sm text-gray-600 mb-2">SCAS: +352 4796 4500</p>
                <p className="text-sm text-gray-700">Can provide emergency financial aid and housing referrals</p>
              </div>
              <div className="bg-white rounded-xl p-4">
                <h4 className="font-bold text-gray-900 mb-2">Esch-sur-Alzette</h4>
                <p className="text-sm text-gray-600 mb-2">SCAS: +352 27 54 1</p>
                <p className="text-sm text-gray-700">Emergency housing assistance available</p>
              </div>
              <div className="bg-white rounded-xl p-4">
                <h4 className="font-bold text-gray-900 mb-2">Differdange</h4>
                <p className="text-sm text-gray-600 mb-2">SCAS: +352 58 77 1</p>
                <p className="text-sm text-gray-700">Social housing and financial support</p>
              </div>
            </div>

            <p className="text-sm text-gray-600 mt-4">
              <strong>For other municipalities:</strong> Search "[your municipality] SCAS Luxembourg" or call 
              your town hall (Mairie/Gemeng) and ask for social services.
            </p>
          </div>
        </section>

        {/* Hotels and Temporary Housing */}
        <section className="mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-8 flex items-center gap-3">
            <i className="ri-hotel-line text-orange-600"></i>
            Hotels and Temporary Housing
          </h2>

          <div className="bg-orange-50 rounded-2xl p-8 mb-6">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              If You Need Immediate Accommodation Tonight
            </h3>

            <div className="bg-white rounded-xl p-6 mb-6">
              <h4 className="font-bold text-gray-900 mb-4">Budget-Friendly Hotels in Luxembourg City:</h4>
              <div className="space-y-4">
                <div className="flex items-start gap-4 pb-4 border-b border-gray-200">
                  <i className="ri-hotel-bed-line text-2xl text-orange-600 flex-shrink-0"></i>
                  <div className="flex-1">
                    <h5 className="font-bold text-gray-900">Ibis Budget Luxembourg Sud</h5>
                    <p className="text-sm text-gray-600 mb-2">From €60/night • +352 49 00 81</p>
                    <p className="text-sm text-gray-700">Basic but clean rooms, no questions asked</p>
                  </div>
                </div>

                <div className="flex items-start gap-4 pb-4 border-b border-gray-200">
                  <i className="ri-hotel-bed-line text-2xl text-orange-600 flex-shrink-0"></i>
                  <div className="flex-1">
                    <h5 className="font-bold text-gray-900">Hotel Christophe Colomb</h5>
                    <p className="text-sm text-gray-600 mb-2">From €70/night • +352 40 84 41</p>
                    <p className="text-sm text-gray-700">Central location, weekly rates available</p>
                  </div>
                </div>

                <div className="flex items-start gap-4 pb-4 border-b border-gray-200">
                  <i className="ri-hotel-bed-line text-2xl text-orange-600 flex-shrink-0"></i>
                  <div className="flex-1">
                    <h5 className="font-bold text-gray-900">Youth Hostel Luxembourg City</h5>
                    <p className="text-sm text-gray-600 mb-2">From €35/night • +352 22 68 89 1</p>
                    <p className="text-sm text-gray-700">Private rooms available, safe environment</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <i className="ri-hotel-bed-line text-2xl text-orange-600 flex-shrink-0"></i>
                  <div className="flex-1">
                    <h5 className="font-bold text-gray-900">Hotel Carlton</h5>
                    <p className="text-sm text-gray-600 mb-2">From €80/night • +352 29 96 60</p>
                    <p className="text-sm text-gray-700">Near train station, discreet check-in</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-yellow-50 border-l-4 border-yellow-500 p-6 rounded-xl">
              <h4 className="font-bold text-gray-900 mb-3">💡 Money-Saving Tips:</h4>
              <ul className="space-y-2 text-gray-700">
                <li>• <strong>Ask for weekly rates</strong> - Usually 20-30% cheaper than daily</li>
                <li>• <strong>Mention you're in an emergency</strong> - Some hotels offer discounts</li>
                <li>• <strong>Contact SCAS</strong> - They may cover hotel costs for first few nights</li>
                <li>• <strong>Use booking.com or hotels.com</strong> - Often cheaper than calling directly</li>
                <li>• <strong>Stay outside city center</strong> - Hotels in Esch or Differdange are cheaper</li>
              </ul>
            </div>
          </div>

          {/* Airbnb Alternative */}
          <div className="bg-purple-50 rounded-2xl p-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Short-Term Rentals (Airbnb, Booking.com)
            </h3>
            <p className="text-gray-700 mb-4">
              For stays longer than a few days, short-term rentals can be more affordable than hotels.
            </p>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white rounded-xl p-6">
                <h4 className="font-bold text-gray-900 mb-3">Advantages:</h4>
                <ul className="space-y-2 text-gray-700">
                  <li>✅ Kitchen to cook (saves money)</li>
                  <li>✅ More privacy and space</li>
                  <li>✅ Weekly/monthly discounts</li>
                  <li>✅ Can be cheaper than hotels</li>
                  <li>✅ More like a real home</li>
                </ul>
              </div>

              <div className="bg-white rounded-xl p-6">
                <h4 className="font-bold text-gray-900 mb-3">Average Costs:</h4>
                <ul className="space-y-2 text-gray-700">
                  <li><strong>Studio apartment:</strong> €50-80/night</li>
                  <li><strong>Weekly rate:</strong> €300-500/week</li>
                  <li><strong>Monthly rate:</strong> €1000-1500/month</li>
                  <li className="text-sm text-gray-600 mt-3">
                    💡 Tip: Message hosts explaining your situation—many will offer discounts
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Financial Assistance */}
        <section className="mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-8 flex items-center gap-3">
            <i className="ri-money-euro-circle-line text-emerald-600"></i>
            Financial Assistance for Housing
          </h2>

          <div className="bg-emerald-50 rounded-2xl p-8 mb-6">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">
              Revenu d'Inclusion Sociale (REVIS)
            </h3>

            <div className="bg-white rounded-xl p-6 mb-6">
              <p className="text-gray-700 mb-4">
                If you have little or no income, REVIS provides financial support to cover basic needs 
                including housing, food, and utilities.
              </p>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-bold text-gray-900 mb-3">Monthly Amounts (2025):</h4>
                  <ul className="space-y-2 text-gray-700">
                    <li><strong>Single person:</strong> €1,542/month</li>
                    <li><strong>Single parent + 1 child:</strong> €2,313/month</li>
                    <li><strong>Single parent + 2 children:</strong> €2,698/month</li>
                    <li className="text-sm text-emerald-700 mt-3">
                      Plus additional housing allowance up to €600/month
                    </li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-bold text-gray-900 mb-3">How to Apply:</h4>
                  <ul className="space-y-2 text-gray-700">
                    <li>1. Contact your municipal SCAS office</li>
                    <li>2. Bring ID and proof of situation</li>
                    <li>3. They'll help you fill out forms</li>
                    <li>4. Emergency payments can start within days</li>
                    <li>5. Regular payments start within 2-4 weeks</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="bg-emerald-100 rounded-xl p-6">
              <h4 className="font-bold text-gray-900 mb-3">What REVIS Covers:</h4>
              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <i className="ri-home-4-line text-2xl text-emerald-600 mb-2"></i>
                  <p className="font-bold text-gray-900">Housing</p>
                  <p className="text-sm text-gray-700">Rent and utilities</p>
                </div>
                <div>
                  <i className="ri-restaurant-line text-2xl text-emerald-600 mb-2"></i>
                  <p className="font-bold text-gray-900">Food</p>
                  <p className="text-sm text-gray-700">Groceries and meals</p>
                </div>
                <div>
                  <i className="ri-health-book-line text-2xl text-emerald-600 mb-2"></i>
                  <p className="font-bold text-gray-900">Healthcare</p>
                  <p className="text-sm text-gray-700">Medical expenses</p>
                </div>
              </div>
            </div>
          </div>

          {/* Rent Subsidies */}
          <div className="bg-blue-50 rounded-2xl p-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Subvention de Loyer (Rent Subsidy)
            </h3>
            <p className="text-gray-700 mb-6">
              Even if you're working but struggling to afford rent, you may qualify for rent subsidies.
            </p>

            <div className="bg-white rounded-xl p-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-bold text-gray-900 mb-3">Eligibility:</h4>
                  <ul className="space-y-2 text-gray-700">
                    <li>✅ Rent exceeds 25% of your income</li>
                    <li>✅ Income below certain thresholds</li>
                    <li>✅ Legal resident of Luxembourg</li>
                    <li>✅ Renting on the private market</li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-bold text-gray-900 mb-3">Subsidy Amount:</h4>
                  <ul className="space-y-2 text-gray-700">
                    <li><strong>Up to €300/month</strong> for rent</li>
                    <li>Calculated based on income and family size</li>
                    <li>Paid directly to you monthly</li>
                    <li>Can be combined with REVIS</li>
                  </ul>
                </div>
              </div>

              <div className="mt-6 pt-6 border-t border-gray-200">
                <h4 className="font-bold text-gray-900 mb-3">Apply at:</h4>
                <p className="text-gray-700">
                  <strong>Ministère du Logement</strong><br />
                  Phone: +352 247 86 100<br />
                  Email: info@ml.etat.lu<br />
                  Website: logement.public.lu
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Long-Term Housing Solutions */}
        <section className="mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-8 flex items-center gap-3">
            <i className="ri-building-2-line text-indigo-600"></i>
            Long-Term Housing Solutions
          </h2>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Social Housing */}
            <div className="bg-indigo-50 rounded-2xl p-6">
              <div className="w-12 h-12 bg-indigo-600 rounded-full flex items-center justify-center mb-4">
                <i className="ri-community-line text-xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Social Housing</h3>
              <p className="text-gray-700 mb-4">
                Affordable apartments managed by municipalities and housing associations.
              </p>
              <ul className="space-y-2 text-gray-700 mb-4">
                <li>• Rent: €400-800/month</li>
                <li>• Priority for violence victims</li>
                <li>• Long-term contracts</li>
                <li>• Apply through Fonds du Logement</li>
              </ul>
              <p className="text-sm text-indigo-900 font-medium">
                Waiting time: 2-6 months (faster with priority status)
              </p>
            </div>

            {/* Housing Cooperatives */}
            <div className="bg-purple-50 rounded-2xl p-6">
              <div className="w-12 h-12 bg-purple-600 rounded-full flex items-center justify-center mb-4">
                <i className="ri-group-line text-xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Housing Cooperatives</h3>
              <p className="text-gray-700 mb-4">
                Community-owned housing with affordable rents and supportive environment.
              </p>
              <ul className="space-y-2 text-gray-700 mb-4">
                <li>• Rent: €500-900/month</li>
                <li>• Community support</li>
                <li>• Stable long-term housing</li>
                <li>• Contact: CIGL (+352 45 35 35)</li>
              </ul>
              <p className="text-sm text-purple-900 font-medium">
                Waiting time: 3-12 months
              </p>
            </div>

            {/* Shared Housing */}
            <div className="bg-pink-50 rounded-2xl p-6">
              <div className="w-12 h-12 bg-pink-600 rounded-full flex items-center justify-center mb-4">
                <i className="ri-home-heart-line text-xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Shared Housing</h3>
              <p className="text-gray-700 mb-4">
                Share an apartment with other women in similar situations.
              </p>
              <ul className="space-y-2 text-gray-700 mb-4">
                <li>• Rent: €300-600/month per room</li>
                <li>• Shared costs (utilities, internet)</li>
                <li>• Emotional support from housemates</li>
                <li>• Check: wg-gesucht.lu, athome.lu</li>
              </ul>
              <p className="text-sm text-pink-900 font-medium">
                Available: Immediately to 1 month
              </p>
            </div>

            {/* Private Rental */}
            <div className="bg-teal-50 rounded-2xl p-6">
              <div className="w-12 h-12 bg-teal-600 rounded-full flex items-center justify-center mb-4">
                <i className="ri-key-line text-xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Private Rental Market</h3>
              <p className="text-gray-700 mb-4">
                Rent directly from private landlords.
              </p>
              <ul className="space-y-2 text-gray-700 mb-4">
                <li>• Rent: €800-1500/month (studio/1BR)</li>
                <li>• More options available</li>
                <li>• Faster to secure</li>
                <li>• Check: athome.lu, immotop.lu</li>
              </ul>
              <p className="text-sm text-teal-900 font-medium">
                💡 Tip: Mention you have REVIS/rent subsidy—landlords like guaranteed payment
              </p>
            </div>
          </div>
        </section>

        {/* Support Services */}
        <section className="mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-8 flex items-center gap-3">
            <i className="ri-hand-heart-line text-rose-600"></i>
            Additional Support Services
          </h2>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-rose-50 rounded-2xl p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Legal Support</h3>
              <div className="space-y-4">
                <div>
                  <h4 className="font-bold text-gray-900 mb-2">Ordre des Avocats</h4>
                  <p className="text-sm text-gray-700 mb-2">Free legal consultation for violence victims</p>
                  <p className="text-sm text-gray-600">Phone: +352 44 30 99 | Email: info@ola.lu</p>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-2">Femmes en Détresse Legal Team</h4>
                  <p className="text-sm text-gray-700 mb-2">Specialized in domestic violence cases</p>
                  <p className="text-sm text-gray-600">Phone: 40 81 81</p>
                </div>
              </div>
            </div>

            <div className="bg-blue-50 rounded-2xl p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Psychological Support</h3>
              <div className="space-y-4">
                <div>
                  <h4 className="font-bold text-gray-900 mb-2">SOS Détresse</h4>
                  <p className="text-sm text-gray-700 mb-2">24/7 crisis helpline</p>
                  <p className="text-sm text-gray-600">Phone: 45 45 45</p>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-2">Ligue Luxembourgeoise d'Hygiène Mentale</h4>
                  <p className="text-sm text-gray-700 mb-2">Free counseling services</p>
                  <p className="text-sm text-gray-600">Phone: 45 00 32</p>
                </div>
              </div>
            </div>

            <div className="bg-green-50 rounded-2xl p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Children's Support</h3>
              <div className="space-y-4">
                <div>
                  <h4 className="font-bold text-gray-900 mb-2">Kanner-Jugendtelefon</h4>
                  <p className="text-sm text-gray-700 mb-2">Helpline for children and teens</p>
                  <p className="text-sm text-gray-600">Phone: 116 111 (free, 24/7)</p>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-2">Femmes en Détresse Children's Services</h4>
                  <p className="text-sm text-gray-700 mb-2">Therapy and support for children who witnessed violence</p>
                  <p className="text-sm text-gray-600">Phone: 40 81 81</p>
                </div>
              </div>
            </div>

            <div className="bg-purple-50 rounded-2xl p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Employment Support</h3>
              <div className="space-y-4">
                <div>
                  <h4 className="font-bold text-gray-900 mb-2">ADEM (Employment Agency)</h4>
                  <p className="text-sm text-gray-700 mb-2">Job search assistance and training</p>
                  <p className="text-sm text-gray-600">Phone: 247 88 888</p>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-2">Caritas Luxembourg</h4>
                  <p className="text-sm text-gray-700 mb-2">Job training and integration programs</p>
                  <p className="text-sm text-gray-600">Phone: 40 21 31 1</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Step-by-Step Action Plan */}
        <section className="mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-8 flex items-center gap-3">
            <i className="ri-roadmap-line text-violet-600"></i>
            Your Step-by-Step Action Plan
          </h2>

          <div className="bg-violet-50 rounded-2xl p-8">
            <div className="space-y-6">
              {/* Day 1 */}
              <div className="bg-white rounded-xl p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-violet-600 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-bold">1</span>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-gray-900 mb-3">Day 1: Get to Safety</h3>
                    <ul className="space-y-2 text-gray-700">
                      <li>✅ Call Femmes en Détresse: 40 81 81 (or Police: 113 if in immediate danger)</li>
                      <li>✅ Go to their shelter or a hotel (they'll help you decide)</li>
                      <li>✅ Bring: ID, phone, charger, medications, important documents if possible</li>
                      <li>✅ Don't worry about money—they'll help you access emergency funds</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Week 1 */}
              <div className="bg-white rounded-xl p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-violet-600 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-bold">2</span>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-gray-900 mb-3">Week 1: Stabilize Your Situation</h3>
                    <ul className="space-y-2 text-gray-700">
                      <li>✅ Meet with social worker at shelter</li>
                      <li>✅ Apply for REVIS (emergency financial assistance)</li>
                      <li>✅ Contact your insurance (AXA, etc.) about emergency housing coverage</li>
                      <li>✅ File police report if you haven't already (helps with housing priority)</li>
                      <li>✅ Get legal consultation (free through shelter)</li>
                      <li>✅ Start counseling/therapy (available at shelter)</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Week 2-4 */}
              <div className="bg-white rounded-xl p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-violet-600 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-bold">3</span>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-gray-900 mb-3">Weeks 2-4: Plan Your Next Steps</h3>
                    <ul className="space-y-2 text-gray-700">
                      <li>✅ Apply to Fonds du Logement for social housing (mention priority status)</li>
                      <li>✅ Apply for rent subsidy if you're working</li>
                      <li>✅ Start looking at private rentals (athome.lu, immotop.lu)</li>
                      <li>✅ Contact ADEM if you need job support</li>
                      <li>✅ Set up protection order if needed (lawyer will help)</li>
                      <li>✅ Continue therapy and support groups</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Month 2-3 */}
              <div className="bg-white rounded-xl p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-violet-600 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-bold">4</span>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-gray-900 mb-3">Months 2-3: Move to Permanent Housing</h3>
                    <ul className="space-y-2 text-gray-700">
                      <li>✅ Accept social housing offer or secure private rental</li>
                      <li>✅ Use REVIS/rent subsidy for deposit and first month's rent</li>
                      <li>✅ Move into your new home with support from shelter staff</li>
                      <li>✅ Set up utilities, internet, etc.</li>
                      <li>✅ Continue counseling and support groups</li>
                      <li>✅ Start rebuilding your life—you've got this! 💜</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Important Reminders */}
        <section className="mb-16">
          <div className="bg-gradient-to-br from-purple-600 to-pink-600 rounded-2xl p-8 text-white">
            <h2 className="text-3xl font-bold mb-6 flex items-center gap-3">
              <i className="ri-heart-line"></i>
              Important Reminders
            </h2>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white/10 backdrop-blur rounded-xl p-6">
                <h3 className="text-xl font-bold mb-3">You Don't Need Money to Leave</h3>
                <p className="text-white/90">
                  Femmes en Détresse provides free shelter, food, and support. REVIS provides emergency 
                  financial assistance within days. You can leave even with €0 in your pocket.
                </p>
              </div>

              <div className="bg-white/10 backdrop-blur rounded-xl p-6">
                <h3 className="text-xl font-bold mb-3">You Don't Need to Have Everything Figured Out</h3>
                <p className="text-white/90">
                  Social workers will help you with every step—housing applications, financial aid, legal 
                  issues, job search. You don't have to do this alone.
                </p>
              </div>

              <div className="bg-white/10 backdrop-blur rounded-xl p-6">
                <h3 className="text-xl font-bold mb-3">Your Children Will Be Safe</h3>
                <p className="text-white/90">
                  Shelters have space for children of all ages. They provide childcare, therapy, and help 
                  with school enrollment. Your kids will be safer with you in a shelter than in a violent home.
                </p>
              </div>

              <div className="bg-white/10 backdrop-blur rounded-xl p-6">
                <h3 className="text-xl font-bold mb-3">You Deserve Better</h3>
                <p className="text-white/90">
                  No one deserves to live in fear. You deserve safety, peace, and a home where you feel 
                  secure. Taking this step is brave, and you're stronger than you know.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Emergency Contacts Summary */}
        <section className="mb-16">
          <div className="bg-red-50 border-2 border-red-600 rounded-2xl p-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
              <i className="ri-phone-line text-red-600"></i>
              Save These Numbers Now
            </h2>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white rounded-xl p-6">
                <h3 className="text-xl font-bold text-red-600 mb-4">Emergency Services</h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex justify-between items-center">
                    <span className="font-bold">Police Emergency</span>
                    <span className="text-red-600 font-bold">113</span>
                  </li>
                  <li className="flex justify-between items-center">
                    <span className="font-bold">Ambulance</span>
                    <span className="text-red-600 font-bold">112</span>
                  </li>
                  <li className="flex justify-between items-center">
                    <span className="font-bold">Women's Helpline</span>
                    <span className="text-red-600 font-bold">2060 1060</span>
                  </li>
                </ul>
              </div>

              <div className="bg-white rounded-xl p-6">
                <h3 className="text-xl font-bold text-purple-600 mb-4">Shelter & Support</h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex justify-between items-center">
                    <span className="font-bold">Femmes en Détresse</span>
                    <span className="text-purple-600 font-bold">40 81 81</span>
                  </li>
                  <li className="flex justify-between items-center">
                    <span className="font-bold">Fraenhaus</span>
                    <span className="text-purple-600 font-bold">44 81 81</span>
                  </li>
                  <li className="flex justify-between items-center">
                    <span className="font-bold">SOS Détresse</span>
                    <span className="text-purple-600 font-bold">45 45 45</span>
                  </li>
                </ul>
              </div>

              <div className="bg-white rounded-xl p-6">
                <h3 className="text-xl font-bold text-blue-600 mb-4">Housing Assistance</h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex justify-between items-center">
                    <span className="font-bold">Fonds du Logement</span>
                    <span className="text-blue-600 font-bold">40 19 40 1</span>
                  </li>
                  <li className="flex justify-between items-center">
                    <span className="font-bold">SCAS Luxembourg City</span>
                    <span className="text-blue-600 font-bold">4796 4500</span>
                  </li>
                </ul>
              </div>

              <div className="bg-white rounded-xl p-6">
                <h3 className="text-xl font-bold text-green-600 mb-4">Insurance Support</h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex justify-between items-center">
                    <span className="font-bold">AXA Emergency</span>
                    <span className="text-green-600 font-bold">44 24 24 1</span>
                  </li>
                  <li className="flex justify-between items-center">
                    <span className="font-bold">Foyer Emergency</span>
                    <span className="text-green-600 font-bold">437 43 1</span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="mt-6 bg-yellow-50 border-l-4 border-yellow-500 p-4 rounded">
              <p className="text-sm text-yellow-900">
                <strong>💡 Tip:</strong> Save these numbers in your phone under different names 
                (like "Pizza Place" or "Dentist") if you're worried about someone checking your phone.
              </p>
            </div>
          </div>
        </section>

        {/* Call to Action */}
        <section className="mb-16">
          <div className="bg-gradient-to-br from-pink-500 to-purple-600 rounded-2xl p-12 text-center text-white">
            <i className="ri-heart-3-line text-6xl mb-6"></i>
            <h2 className="text-4xl font-bold mb-6">
              You Don't Have to Do This Alone
            </h2>
            <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
              Thousands of women in Luxembourg have walked this path before you. They found safety, 
              rebuilt their lives, and are now thriving. You can too.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Link
                to="/safety"
                className="px-8 py-4 bg-white text-purple-600 rounded-full font-bold hover:bg-gray-100 transition-colors whitespace-nowrap"
              >
                Access Safety Tools
              </Link>
              <Link
                to="/donate"
                className="px-8 py-4 bg-purple-800 text-white rounded-full font-bold hover:bg-purple-900 transition-colors whitespace-nowrap"
              >
                Support Women Like You
              </Link>
            </div>
          </div>
        </section>

        {/* Share Section */}
        <section className="border-t border-gray-200 pt-12">
          <div className="text-center mb-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Share This Resource
            </h3>
            <p className="text-gray-600 mb-6">
              This information could save someone's life. Please share it.
            </p>
            <div className="flex gap-4 justify-center">
              <button className="w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center hover:bg-blue-700 transition-colors">
                <i className="ri-facebook-fill text-xl"></i>
              </button>
              <button className="w-12 h-12 bg-sky-500 text-white rounded-full flex items-center justify-center hover:bg-sky-600 transition-colors">
                <i className="ri-twitter-fill text-xl"></i>
              </button>
              <button className="w-12 h-12 bg-green-600 text-white rounded-full flex items-center justify-center hover:bg-green-700 transition-colors">
                <i className="ri-whatsapp-fill text-xl"></i>
              </button>
              <button className="w-12 h-12 bg-blue-700 text-white rounded-full flex items-center justify-center hover:bg-blue-800 transition-colors">
                <i className="ri-linkedin-fill text-xl"></i>
              </button>
              <button className="w-12 h-12 bg-gray-600 text-white rounded-full flex items-center justify-center hover:bg-gray-700 transition-colors">
                <i className="ri-mail-fill text-xl"></i>
              </button>
            </div>
          </div>
        </section>
      </article>
    </div>
  );
}